type FieldKind = 'string' | 'nonEmptyString' | 'number';

// 'nonEmptyString' marks the fields where an empty value is a misdeploy the
// fetch-before-bootstrap gate must catch; sentryDsn stays 'string' because ''
// legitimately means Sentry-off (see infra/stages.ts).
const FIELD_KINDS = {
  apiUrl: 'nonEmptyString',
  auth0Domain: 'nonEmptyString',
  auth0ClientId: 'nonEmptyString',
  auth0ApiAudience: 'nonEmptyString',
  sentryDsn: 'string',
  sentryEnvironment: 'string',
  sentryRelease: 'string',
  sentryTracesSampleRate: 'number',
  sentryReplaysSessionSampleRate: 'number',
  sentryReplaysOnErrorSampleRate: 'number',
} as const satisfies Record<string, FieldKind>;

export type WebRuntimeConfig = {
  -readonly [K in keyof typeof FIELD_KINDS]: (typeof FIELD_KINDS)[K] extends 'number'
    ? number
    : string;
};

export const WEB_RUNTIME_CONFIG_PATH = '/config.json';

const KIND_LABELS: Record<FieldKind, string> = {
  string: 'string',
  nonEmptyString: 'non-empty string',
  number: 'number',
};

function isValid(value: unknown, kind: FieldKind): boolean {
  if (kind === 'number')
    return typeof value === 'number' && Number.isFinite(value);
  if (typeof value !== 'string') return false;
  return kind === 'string' || value !== '';
}

export function parseWebRuntimeConfig(payload: unknown): WebRuntimeConfig {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload))
    throw new Error(`${WEB_RUNTIME_CONFIG_PATH} is not a JSON object`);

  const blob = payload as Record<string, unknown>;
  const config: Record<string, string | number> = {};

  for (const [key, kind] of Object.entries(FIELD_KINDS)) {
    const value = blob[key];
    if (!isValid(value, kind))
      throw new Error(
        `${WEB_RUNTIME_CONFIG_PATH}: ${key} must be a ${KIND_LABELS[kind]}`,
      );
    config[key] = value as string | number;
  }

  return config as WebRuntimeConfig;
}

export async function loadWebRuntimeConfig(
  fetchFn: typeof fetch = fetch,
): Promise<WebRuntimeConfig> {
  const response = await fetchFn(WEB_RUNTIME_CONFIG_PATH, {
    cache: 'no-cache',
  });

  if (!response.ok)
    throw new Error(
      `${WEB_RUNTIME_CONFIG_PATH} request failed with ${response.status}`,
    );

  return parseWebRuntimeConfig(await response.json());
}
